/* eslint-disable no-underscore-dangle */
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { Button, Form } from 'react-bootstrap';
import BillingoService from '../../Services/BillingoService';
import PaymentsService from '../../Services/PaymentsService';
import {
  DOC_TYPES,
  advanceBlockedReason,
  apiErrorText,
  bookingsToLineItems,
  buildSzamlaWithDeductions,
  documentTotals,
  defaultInvoiceMeta,
  invoicedByBooking,
  isLiveDocument,
  accountSearchTerms,
  priorAccountId,
  recommendedDocType,
} from '../../Utils/billingoDocuments';
import {
  orderLineDrift, predecessorDocument, prefillSources,
} from '../../Utils/billingoPrefill';
import BillingoDocumentTypePicker from './BillingoDocumentTypePicker';
import BillingoAccountSelector from './BillingoAccountSelector';
import BillingoLineSelector from './BillingoLineSelector';
import BillingoLineItemsEditor from './BillingoLineItemsEditor';
import BillingoReviewPanel from './BillingoReviewPanel';
import RecordPaymentModal from '../../Pages/Payments/Components/RecordPaymentModal';
import EditAllocationModal from '../../Pages/Payments/Components/EditAllocationModal';

const STEPS = [
  { key: 'type', label: 'Document' },
  { key: 'account', label: 'Account' },
  { key: 'lines', label: 'Lines' },
  { key: 'review', label: 'Review' },
];

const uniq = (arr) => [...new Set(arr.filter(Boolean))];
const ymd = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

// Reusable Billingo document flow. `context.mode` is 'order' | 'wizard' |
// 'payment'; the same component backs every mount point.
function BillingoDocumentFlow({ context, onComplete, onCancel }) {
  const bookings = useMemo(
    () => context.bookings || (context.order ? [context.order] : []),
    [context.bookings, context.order],
  );
  // Documents already on the order, plus any created during this session — so
  // the Számla can deduct an Előlegszámla issued moments earlier in the chain.
  const [localDocs, setLocalDocs] = useState(context.existingDocs || []);
  // EVERY live advance, spent or not. `advance_consumption` rides along so
  // one list can both be shown to the operator (BillingoAdvancesCard) and
  // handed to buildSzamlaWithDeductions, which drops the spent ones itself.
  // Filtering them out here instead would make the spent advance invisible,
  // which is the bug: Billingo keeps the parent→child link only on the child,
  // so a spent advance looks free until it is refused.
  const priorAdvances = useMemo(
    () => localDocs
      .filter((d) => d.type === DOC_TYPES.ELOLEGSZAMLA && isLiveDocument(d))
      .map((d) => ({
        id: d.id,
        number: d.number,
        total: d.total,
        currency: d.currency,
        order_reference: d.order_references?.[0],
        advance_consumption: d.advance_consumption,
      })),
    [localDocs],
  );

  const candidates = useMemo(() => bookingsToLineItems(bookings), [bookings]);
  const invoicedMap = useMemo(() => invoicedByBooking(localDocs), [localDocs]);

  // A Billingo document is single-currency. The user picks one of the currencies
  // present on the lines; only lines in that currency are billed (no FX). Default
  // to the order's booking currency when it's among them.
  const availableCurrencies = useMemo(
    () => uniq(candidates.map((c) => c.currency)),
    [candidates],
  );
  // Keys of every candidate line in a given currency (used to (re)select the
  // whole currency when it changes or the chain continues).
  const keysForCurrency = (cur) => new Set(
    candidates.filter((c) => c.currency === cur).map((c) => c.key),
  );

  // What the order needs next, from everything live on it — including a
  // document created moments ago in this flow. The type step starts on it and
  // badges it, and the success view offers it as the next document.
  const recommended = useMemo(
    () => recommendedDocType(localDocs, { mode: context.mode, bookings }),
    [localDocs, context.mode, bookings],
  );

  const [step, setStep] = useState('type');
  const [docType, setDocType] = useState(recommended);
  const [currency, setCurrency] = useState(() => {
    const orderCur = context.order?.currency;
    const cands = bookingsToLineItems(bookings).map((c) => c.currency);
    if (orderCur && cands.includes(orderCur)) return orderCur;
    return cands[0] || orderCur || 'EUR';
  });
  // Pre-select the partner's saved Billingo account (overridable). Falls back to
  // any account the launcher passed.
  //
  // DO NOT WIDEN THIS INTO "the account this partner last used". Measured in
  // production 2026-11-02 over 1,157 invoices with an account and a booking,
  // across 45 partners: 38 of 45 partners (84.4%) always use the same account,
  // but those are only 159 documents — 13.7%. Partner c455727d, the house
  // "Pickup Hungary" partner, has 383 DISTINCT Billingo accounts over 924
  // documents, i.e. 80% of everything issued, because direct retail gives each
  // end customer their own Billingo party. Reusing a partner's last account
  // would therefore be right for 13.7% of documents and would stamp a previous,
  // unrelated customer's billing identity onto a new person's tax document for
  // most of the rest. A wrong counterparty on a fiscal document is the worst
  // failure this flow has.
  //
  // `partner.billingo_account_id` above is a DELIBERATE configuration on the
  // partner, not a history lookup, which is why it is safe for the 38 B2B
  // partners. It carries the same hazard if it is ever set on the house
  // partner — see the report; nobody should set one there.
  //
  // What the 80% actually needs is for the SEARCH to be seeded, not answered.
  // That is the effect below: accountSearchTerms() searches by the customer's
  // own billing name, email and passenger name and offers the hits as
  // one-click picks, so the agent confirms a party instead of typing one.
  const [account, setAccount] = useState(
    context.account
    || (context.partner?.billingo_account_id
      ? { id: context.partner.billingo_account_id, name: context.partner.billingo_account_name }
      : null),
  );
  const [selectedKeys, setSelectedKeys] = useState(() => keysForCurrency(currency));
  const [edits, setEdits] = useState({});
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [createdDoc, setCreatedDoc] = useState(null);
  // The invoice can be tied to a real payment (Számla/Előlegszámla). Start from
  // any payment the flow was launched with; the review step can pick another
  // booking payment or record a new one (reusing RecordPaymentModal).
  const [paymentId, setPaymentId] = useState(context.payment?._id || null);
  const [knownPayments, setKnownPayments] = useState(context.payments || []);
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  // The payment whose order-line split is being re-cut, in the modal the
  // Payments page and the order's Financial tab already use. Before this, the
  // review step's only payment control was "Record new" — which is why the
  // same Stripe charge was keyed in by hand 99 times (see
  // paymentAllocationGap).
  const [reallocating, setReallocating] = useState(null);
  // Existing Billingo partners that look like this customer, offered as
  // one-click picks instead of making the agent retype the search.
  const [accountSuggestions, setAccountSuggestions] = useState([]);
  const [accountNote, setAccountNote] = useState('');

  // Prefill the Billingo account. The party is very often already in Billingo —
  // either invoiced on this same order before, or created for an earlier trip —
  // and re-creating it produces duplicate partners. Search by the customer's own
  // details and:
  //   - auto-select when a hit IS the account a previous document for these
  //     bookings used (same order, same party — no ambiguity);
  //   - otherwise offer the hits as suggestions for the agent to confirm,
  //     because a name match alone is not proof of the same legal entity.
  // Runs once per flow: continuing the chain (Díjbekérő -> Előlegszámla ->
  // Számla) must not re-prefill over the account the agent already picked.
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (prefilledRef.current || account) return undefined;
    const terms = accountSearchTerms(bookings);
    if (terms.length === 0) return undefined;
    prefilledRef.current = true;

    let cancelled = false;
    const prior = priorAccountId(context.existingDocs || []);
    // Promise.resolve(): prefill is best-effort, so a service that returns
    // nothing usable must not break opening the flow.
    Promise.all(terms.map((t) => Promise.resolve(BillingoService.searchAccounts(t))
      .then((r) => r || [])
      .catch(() => [])))
      .then((lists) => {
        if (cancelled) return;
        const seen = new Set();
        const merged = [];
        lists.flat().forEach((a) => {
          if (a && a.id && !seen.has(a.id)) { seen.add(a.id); merged.push(a); }
        });
        const previouslyUsed = merged.find((a) => a.id === prior);
        if (previouslyUsed) {
          setAccount(previouslyUsed);
          setAccountNote('Reused from an earlier document on this order.');
        } else {
          setAccountSuggestions(merged.slice(0, 4));
        }
      });
    return () => { cancelled = true; };
  }, [bookings, account, context.existingDocs]);

  // Load the booking(s)' real payments so they can be linked, unless the caller
  // already supplied them (e.g. the per-order panel).
  useEffect(() => {
    let cancelled = false;
    const ids = uniq(bookings.map((b) => b._id)).filter(Boolean);
    if (!(context.payments || []).length && ids.length) {
      Promise.all(ids.map((id) => PaymentsService.listForBooking(id).catch(() => [])))
        .then((lists) => { if (!cancelled) setKnownPayments(lists.flat()); });
    }
    return () => { cancelled = true; };
  }, [bookings, context.payments]);

  // Switching the document currency re-selects all lines in the new currency.
  const handleCurrencyChange = (next) => {
    setCurrency(next);
    setSelectedKeys(keysForCurrency(next));
  };

  const lineItems = useMemo(
    () => candidates
      .filter((c) => c.currency === currency && selectedKeys.has(c.key))
      .map((c) => ({ ...c, ...edits[c.key] })),
    [candidates, currency, selectedKeys, edits],
  );

  const deductionItems = useMemo(
    () => (docType === DOC_TYPES.SZAMLA
      ? buildSzamlaWithDeductions([], priorAdvances, currency)
      : []),
    [docType, priorAdvances, currency],
  );

  const displayedItems = useMemo(
    () => [...lineItems, ...deductionItems],
    [lineItems, deductionItems],
  );

  const totals = useMemo(() => documentTotals(displayedItems), [displayedItems]);
  // What the document BILLS, before advances are netted off — the figure a
  // payment belongs against. `totals` is what it CHARGES.
  const billed = useMemo(() => documentTotals(lineItems), [lineItems]);
  const orderReferences = useMemo(() => uniq(lineItems.map((i) => i.order_reference)), [lineItems]);
  const bookingIds = useMemo(() => uniq(lineItems.map((i) => i.booking_id)), [lineItems]);

  // Type-aware invoice meta (fulfillment/due/payment method + payment date),
  // defaulted from the bookings + payments and overridable on the review step.
  const metaDefaults = useMemo(
    () => defaultInvoiceMeta(docType, bookings, knownPayments),
    [docType, bookings, knownPayments],
  );
  const [metaOverrides, setMetaOverrides] = useState({});
  const meta = { ...metaDefaults, ...metaOverrides };
  const handleMetaChange = (patch) => setMetaOverrides((prev) => ({ ...prev, ...patch }));

  // What this document is a copy of, where each pre-filled value came from,
  // and how the booking has moved since. Sources are DERIVED by comparison
  // (see Utils/billingoPrefill.js) rather than tracked as the flow fills
  // them: `metaOverrides` is written programmatically too — selecting a
  // payment defaults the payment date through it — so it cannot be read as
  // "the operator edited this".
  const predecessor = useMemo(
    () => predecessorDocument(localDocs, docType),
    [localDocs, docType],
  );
  const prefill = useMemo(
    () => prefillSources({
      predecessor,
      account,
      partner: context.partner,
      currency,
      orderCurrency: context.order?.currency,
      meta,
      metaDefaults,
    }),
    // `meta` is rebuilt each render; its two inputs are the real dependencies.
    [predecessor, account, context.partner, currency, context.order, metaOverrides, metaDefaults],
  );
  const drift = useMemo(
    () => orderLineDrift(predecessor, candidates, currency),
    [predecessor, candidates, currency],
  );

  // Why the final Számla must not go out at all — '' when it may. Only an
  // advance Billingo holds a descendant for blocks; an advance a Számla of
  // ours already deducted is simply dropped from the deductions.
  const advanceBlocked = useMemo(
    () => (docType === DOC_TYPES.SZAMLA ? advanceBlockedReason(priorAdvances) : ''),
    [docType, priorAdvances],
  );

  // Selecting a payment also defaults the payment date to that payment's date.
  const handleSelectPayment = (id) => {
    setPaymentId(id);
    const p = id && knownPayments.find((x) => (x._id || x.id) === id);
    if (p && p.occurred_at) handleMetaChange({ payment_date: ymd(p.occurred_at) });
  };

  // Preselect an existing completed payment for the booking (Számla/Előlegszámla).
  useEffect(() => {
    if (paymentId || context.payment) return;
    if (docType !== DOC_TYPES.SZAMLA && docType !== DOC_TYPES.ELOLEGSZAMLA) return;
    const inbound = knownPayments
      .filter((p) => p && p.direction === 'in' && p.status === 'completed')
      .sort((a, b) => new Date(b.occurred_at || 0) - new Date(a.occurred_at || 0));
    const p = inbound[0];
    if (p) {
      setPaymentId(p._id || p.id);
      if (p.occurred_at) {
        setMetaOverrides((prev) => ({ ...prev, payment_date: ymd(p.occurred_at) }));
      }
    }
  }, [knownPayments, docType, paymentId, context.payment]);

  const handleEdit = (key, patch) => setEdits(
    (prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }),
  );
  // Overwrite a field (e.g. description, vat) across every line at once.
  const handleBulkEdit = (patch) => setEdits((prev) => {
    const next = { ...prev };
    lineItems.forEach((it) => { next[it.key] = { ...next[it.key], ...patch }; });
    return next;
  });
  const handleRemove = (key) => setSelectedKeys((prev) => {
    const next = new Set(prev);
    next.delete(key);
    return next;
  });

  const handleCreate = async () => {
    setError('');
    setCreating(true);
    try {
      const doc = await BillingoService.createDocument({
        type: docType,
        account,
        lineItems: displayedItems,
        booking_ids: bookingIds,
        order_references: orderReferences,
        payment_id: paymentId,
        fulfillment_date: meta.fulfillment_date,
        due_date: meta.due_date,
        payment_method: meta.payment_method,
        payment_date: meta.payment_date,
        language: meta.language,
      });
      if (context.mode === 'payment' && context.payment?._id) {
        await BillingoService.assignPaymentToOrder(
          context.payment._id,
          context.order?._id || bookingIds[0],
        );
      }
      setLocalDocs((prev) => [...prev, doc]);
      setCreatedDoc(doc);
    } catch (err) {
      // The reason travels in `errors[]`, not `message` — reading `message`
      // here is what showed a Billingo refusal as "The validation has failed".
      setError(apiErrorText(err, 'Failed to create document'));
    } finally {
      setCreating(false);
    }
  };

  const nextType = createdDoc ? recommended : null;

  // Continue the chain without leaving the flow: reset for the next document
  // type, re-selecting all lines (the just-created doc is already in localDocs,
  // so a Számla will deduct the fresh Előlegszámla).
  const handleCreateNext = () => {
    setCreatedDoc(null);
    setError('');
    setDocType(nextType);
    setSelectedKeys(keysForCurrency(currency));
    setEdits({});
    setStep('lines');
  };

  const stepIndex = STEPS.findIndex((s) => s.key === step);
  const canAdvance = {
    type: !!docType,
    account: !!account,
    lines: lineItems.length > 0,
    review: true,
  }[step];

  const goNext = () => setStep(STEPS[stepIndex + 1].key);
  const goBack = () => setStep(STEPS[stepIndex - 1].key);

  return (
    <div>
      {/* Stepper */}
      <div className="d-flex align-items-center justify-content-center mb-4 px-2" style={{ maxWidth: 560, margin: '0 auto' }}>
        {STEPS.map((s, i) => {
          let state = 'todo';
          if (i < stepIndex) state = 'done';
          else if (i === stepIndex) state = 'active';
          const clickable = i < stepIndex;
          const circle = {
            active: 'bg-primary text-white',
            done: 'bg-success text-white',
            todo: 'bg-light text-muted border',
          }[state];
          return (
            <React.Fragment key={s.key}>
              <button
                type="button"
                onClick={() => clickable && setStep(s.key)}
                disabled={!clickable}
                className="btn p-0 border-0 bg-transparent d-flex flex-column align-items-center"
                style={{ cursor: clickable ? 'pointer' : 'default', minWidth: 72 }}
              >
                <span
                  className={`d-flex align-items-center justify-content-center rounded-circle fw-semibold ${circle}`}
                  style={{ width: 36, height: 36 }}
                >
                  {state === 'done' ? <i className="fa fa-check" /> : i + 1}
                </span>
                <span className={`small mt-1 ${state === 'active' ? 'fw-semibold text-primary' : 'text-muted'}`}>
                  {s.label}
                </span>
              </button>
              {i < STEPS.length - 1 && (
                <span
                  className={i < stepIndex ? 'bg-success' : 'bg-light'}
                  style={{
                    height: 2, flex: 1, maxWidth: 72, marginBottom: 20,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {step === 'type' && (
        <BillingoDocumentTypePicker
          value={docType}
          onChange={setDocType}
          recommended={recommended}
          existingDocs={localDocs}
        />
      )}

      {step === 'account' && (
        <BillingoAccountSelector
          value={account}
          onChange={(next) => { setAccount(next); setAccountNote(''); }}
          defaultBilling={context.order?.billing || context.partner?.billing}
          suggestions={accountSuggestions}
          note={accountNote}
        />
      )}

      {step === 'lines' && (
        <div>
          {bookings.length === 0 ? (
            <p className="text-muted">No bookings available to invoice.</p>
          ) : (
            <>
              {availableCurrencies.length > 1 && (
                <Form.Group className="mb-3" style={{ maxWidth: 260 }}>
                  <Form.Label className="small text-muted mb-1">
                    Document currency — only lines in this currency are billed
                  </Form.Label>
                  <Form.Select
                    value={currency}
                    onChange={(e) => handleCurrencyChange(e.target.value)}
                  >
                    {availableCurrencies.map((c) => <option key={c} value={c}>{c}</option>)}
                  </Form.Select>
                </Form.Group>
              )}
              <h6 className="text-muted">Select what to invoice</h6>
              <BillingoLineSelector
                bookings={bookings}
                currency={currency}
                selectedKeys={selectedKeys}
                onChange={setSelectedKeys}
                invoicedMap={invoicedMap}
                documents={localDocs}
                payments={knownPayments}
              />
              <h6 className="text-muted mt-4">Lines on the document</h6>
              <BillingoLineItemsEditor
                items={lineItems}
                deductionItems={deductionItems}
                totals={totals}
                onChange={handleEdit}
                onBulkChange={handleBulkEdit}
                onRemove={handleRemove}
              />
            </>
          )}
        </div>
      )}

      {step === 'review' && (
        <BillingoReviewPanel
          docType={docType}
          account={account}
          totals={totals}
          orderReferences={orderReferences}
          context={context}
          creating={creating}
          error={error}
          createdDoc={createdDoc}
          meta={meta}
          onMetaChange={handleMetaChange}
          payments={knownPayments}
          paymentId={paymentId}
          onSelectPayment={handleSelectPayment}
          onRecordNew={() => setShowRecordPayment(true)}
          onEditAllocation={setReallocating}
          advances={priorAdvances}
          advanceBlocked={advanceBlocked}
          currency={currency}
          bookingIds={bookingIds}
          billedGross={billed.byCurrency[currency]?.gross ?? null}
          predecessor={predecessor}
          prefillSources={prefill}
          drift={drift}
          nextType={nextType}
          onCreateNext={handleCreateNext}
          onCreate={handleCreate}
          onClose={() => onComplete(createdDoc)}
        />
      )}

      {/* Footer nav — hidden once the document is created (success view owns its button) */}
      {!createdDoc && (
        <div className="d-flex justify-content-between mt-4 pt-3 border-top">
          <Button
            variant="outline-secondary"
            onClick={stepIndex === 0 ? onCancel : goBack}
          >
            {stepIndex === 0 ? 'Cancel' : 'Back'}
          </Button>
          {step !== 'review' && (
            <Button onClick={goNext} disabled={!canAdvance}>
              Continue
            </Button>
          )}
        </div>
      )}

      <RecordPaymentModal
        show={showRecordPayment}
        onHide={() => setShowRecordPayment(false)}
        onRecorded={(payment) => {
          const id = payment?._id || payment?.id;
          if (id) {
            setKnownPayments((prev) => [payment, ...prev]);
            setPaymentId(id);
            if (payment.occurred_at) {
              setMetaOverrides((prev) => ({ ...prev, payment_date: ymd(payment.occurred_at) }));
            }
          }
          setShowRecordPayment(false);
        }}
      />

      <EditAllocationModal
        show={!!reallocating}
        onHide={() => setReallocating(null)}
        payment={reallocating || {}}
        onEdited={(updated) => {
          const id = updated?._id || updated?.id;
          // Keep the picker's copy in step, or the gap notice would still be
          // read off the pre-split payment the operator just fixed.
          if (id) {
            setKnownPayments((prev) => prev.map((p) => ((p._id || p.id) === id ? updated : p)));
          }
          setReallocating(null);
        }}
      />
    </div>
  );
}

export default BillingoDocumentFlow;
